import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useAuth0Data } from '../../../context/Auth0DataContext';
import { format, parseISO } from 'date-fns';
import CreateTaskModal from '../../../components/ProjectManagement/CreateTaskModal';

const { FiTrello, FiPlus, FiUser, FiCalendar, FiEdit, FiTrash2, FiSave, FiX, FiSettings, FiTarget, FiCheckSquare, FiInfinity, FiEdit3 } = FiIcons;

// Default columns to use when none exist in database
const DEFAULT_COLUMNS = [
  { id: 'planning', title: 'Planning', color: 'bg-blue-100' },
  { id: 'in-progress', title: 'In Progress', color: 'bg-yellow-100' },
  { id: 'review', title: 'Review', color: 'bg-purple-100' },
  { id: 'completed', title: 'Completed', color: 'bg-green-100' }
];

const ProjectBoard = () => {
  const { data, addItem, updateItem, deleteItem, getAllUsers, user } = useAuth0Data();
  const [draggedProject, setDraggedProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingColumns, setEditingColumns] = useState(false);
  const [editingColumnId, setEditingColumnId] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState('all');
  const [projectFormData, setProjectFormData] = useState({
    title: '',
    description: '',
    notes: '',
    owner: '',
    dueDate: '',
    status: 'planning',
    priority: 'medium',
    ongoing: false
  });

  const [newColumn, setNewColumn] = useState({ title: '', color: 'bg-gray-100' });
  const [editingColumnData, setEditingColumnData] = useState({ title: '', color: 'bg-gray-100' });

  // Get columns from context, use defaults if empty (defaults are initialized in Auth0DataContext)
  const columns = useMemo(() => {
    return data.projectColumns && data.projectColumns.length > 0
      ? data.projectColumns
      : DEFAULT_COLUMNS;
  }, [data.projectColumns]);

  const allUsers = useMemo(() => getAllUsers(), [getAllUsers]);

  // Convert column title to status slug (e.g., "In Progress" -> "in-progress")
  const titleToSlug = (title) => {
    return title.toLowerCase().replace(/\s+/g, '-');
  };

  // Memoize filtered projects by task assignee
  const filteredProjects = useMemo(() => {
    if (!data || !data.projects || !Array.isArray(data.projects)) {
      return [];
    }
    if (selectedAssignee === 'all') {
      return data.projects;
    }
    // Filter to projects that have at least one task assigned to the selected person
    return data.projects.filter(project =>
      project.tasks?.some(task => task.assignee === selectedAssignee)
    );
  }, [data.projects, selectedAssignee]);

  // Get projects by status from the filtered list (excluding ongoing projects)
  const getProjectsByStatus = (columnTitle) => {
    const statusSlug = titleToSlug(columnTitle);
    return filteredProjects.filter(project => project && project.status === statusSlug && project.dueDate);
  };

  // Get ongoing projects (no due date) from the filtered list
  const ongoingProjects = useMemo(() => {
    return filteredProjects.filter(project => project && !project.dueDate);
  }, [filteredProjects]);

  const handleDragStart = (e, project) => {
    if (!project) return;
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, columnTitle) => {
    e.preventDefault();
    const newStatus = titleToSlug(columnTitle);
    if (draggedProject && draggedProject.status !== newStatus) {
      updateItem('projects', draggedProject.id, { ...draggedProject, status: newStatus });
    }
    setDraggedProject(null);
  };

  const handleEditProject = (project) => {
    if (!project) return;
    setEditingProject(project.id);
    setProjectFormData({
      title: project.title || '',
      description: project.description || '',
      notes: project.notes || '',
      owner: project.owner || '',
      dueDate: project.dueDate || '',
      status: project.status || 'planning',
      priority: project.priority || 'medium',
      ongoing: !project.dueDate
    });
  };

  const handleSaveProject = () => {
    if (projectFormData.title.trim() && projectFormData.description.trim()) {
      const updatedProject = {
        ...projectFormData,
        dueDate: projectFormData.ongoing ? null : projectFormData.dueDate
      };
      updateItem('projects', editingProject, updatedProject);
      setEditingProject(null);
      resetProjectForm();
    }
  };

  const handleAddProject = (columnTitle) => {
    if (!projectFormData.title.trim()) {
      alert('Project title is required');
      return;
    }
    if (!projectFormData.description.trim()) {
      alert('Project description is required');
      return;
    }
    if (!projectFormData.owner.trim()) {
      alert('Project owner is required');
      return;
    }
    if (!projectFormData.ongoing && !projectFormData.dueDate) {
      alert('Due date is required (or mark as ongoing)');
      return;
    }

    const newProject = {
      title: projectFormData.title.trim(),
      description: projectFormData.description.trim(),
      notes: projectFormData.notes.trim(),
      owner: projectFormData.owner,
      dueDate: projectFormData.ongoing ? null : projectFormData.dueDate,
      status: titleToSlug(columnTitle),
      priority: projectFormData.priority,
      tasks: []
    };

    addItem('projects', newProject);
    setShowProjectForm(null);
    resetProjectForm();
  };

  const resetProjectForm = () => {
    setProjectFormData({
      title: '',
      description: '',
      notes: '',
      owner: '',
      dueDate: '',
      status: 'planning',
      priority: 'medium',
      ongoing: false
    });
  };

  const handleCancelProjectForm = () => {
    setShowProjectForm(null);
    setEditingProject(null);
    resetProjectForm();
  };

  const handleDeleteTask = (project, taskId) => {
    if (confirm('Are you sure you want to delete this task?')) {
      const updatedTasks = (project.tasks || []).filter(t => t.id !== taskId);
      updateItem('projects', project.id, { ...project, tasks: updatedTasks });
    }
  };

  const handleToggleTaskStatus = (project, task) => {
    const newStatus = task.status === 'completed' ? 'todo' : 'completed';
    const updatedTasks = (project.tasks || []).map(t =>
      t.id === task.id ? { ...t, status: newStatus } : t
    );
    updateItem('projects', project.id, { ...project, tasks: updatedTasks });
  };

  const handleAddColumn = async () => {
    if (newColumn.title.trim()) {
      const newCol = {
        title: newColumn.title,
        color: newColumn.color,
        order: columns.length
      };
      try {
        await addItem('projectColumns', newCol);
        setNewColumn({ title: '', color: 'bg-gray-100' });
      } catch (error) {
        console.error('Error adding column:', error);
        alert('Failed to add column. Please try again.');
      }
    }
  };

  const handleEditColumn = (column) => {
    setEditingColumnId(column.id);
    setEditingColumnData({ title: column.title, color: column.color });
  };

  const handleSaveColumn = async () => {
    if (editingColumnData.title.trim()) {
      try {
        await updateItem('projectColumns', editingColumnId, {
          title: editingColumnData.title,
          color: editingColumnData.color
        });
        setEditingColumnId(null);
        setEditingColumnData({ title: '', color: 'bg-gray-100' });
      } catch (error) {
        console.error('Error updating column:', error);
        alert('Failed to update column. Please try again.');
      }
    }
  };

  const handleCancelColumnEdit = () => {
    setEditingColumnId(null);
    setEditingColumnData({ title: '', color: 'bg-gray-100' });
  };

  const handleDeleteColumn = async (columnId) => {
    if (confirm('Are you sure you want to delete this column? Projects in this column will need to be moved.')) {
      try {
        await deleteItem('projectColumns', columnId);
      } catch (error) {
        console.error('Error deleting column:', error);
        alert('Failed to delete column. Please try again.');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning': return 'text-blue-600 bg-blue-100';
      case 'in-progress': return 'text-yellow-600 bg-yellow-100';
      case 'review': return 'text-purple-600 bg-purple-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'on-hold': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Safe check for data existence
  if (!data || !data.projects) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <SafeIcon icon={FiTrello} className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">Loading project board...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrello} className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="ml-3 text-xl font-bold text-gray-900">Project Kanban Board</h3>
          </div>
          <div className="flex items-center gap-2">
            {/* My Tasks Toggle */}
            <button
              onClick={() => setSelectedAssignee(selectedAssignee === user?.name ? 'all' : user?.name || 'all')}
              className={`flex items-center px-3 py-2 rounded-lg border transition-colors ${
                selectedAssignee === user?.name
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiUser} className="w-4 h-4 mr-2" />
              My Tasks
            </button>
            {/* Assignee Filter Dropdown */}
            <select
              value={selectedAssignee}
              onChange={(e) => setSelectedAssignee(e.target.value)}
              className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                selectedAssignee !== 'all' ? 'border-primary-500 bg-primary-50' : 'border-gray-300'
              }`}
            >
              <option value="all">All Assignees</option>
              {user?.name && <option value={user.name}>My Tasks ({user.name})</option>}
              <optgroup label="Team Members">
                {allUsers.filter(u => u.name !== user?.name).map(u => (
                  <option key={u.id} value={u.name}>{u.name}</option>
                ))}
              </optgroup>
            </select>
            <button
              onClick={() => setEditingColumns(!editingColumns)}
              className="flex items-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <SafeIcon icon={FiSettings} className="w-4 h-4 mr-2" />
              Edit Columns
            </button>
            <span className="text-sm text-gray-600">
              {filteredProjects.length} Projects
            </span>
          </div>
        </div>

        {/* Column Editor */}
        {editingColumns && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mb-6 p-4 bg-gray-50 rounded-lg"
          >
            <h4 className="font-semibold text-gray-900 mb-4">Manage Columns</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <input
                type="text"
                value={newColumn.title}
                onChange={(e) => setNewColumn(prev => ({ ...prev, title: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Column title"
              />
              <select
                value={newColumn.color}
                onChange={(e) => setNewColumn(prev => ({ ...prev, color: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="bg-gray-100">Gray</option>
                <option value="bg-blue-100">Blue</option>
                <option value="bg-green-100">Green</option>
                <option value="bg-yellow-100">Yellow</option>
                <option value="bg-red-100">Red</option>
                <option value="bg-purple-100">Purple</option>
              </select>
              <button
                onClick={handleAddColumn}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Add Column
              </button>
            </div>
            <div className="space-y-2">
              {columns.map(column => (
                <div key={column.id} className="flex items-center justify-between p-3 bg-white rounded border">
                  {editingColumnId === column.id ? (
                    <div className="flex items-center gap-3 flex-1">
                      <input
                        type="text"
                        value={editingColumnData.title}
                        onChange={(e) => setEditingColumnData(prev => ({ ...prev, title: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Column title"
                      />
                      <select
                        value={editingColumnData.color}
                        onChange={(e) => setEditingColumnData(prev => ({ ...prev, color: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="bg-gray-100">Gray</option>
                        <option value="bg-blue-100">Blue</option>
                        <option value="bg-green-100">Green</option>
                        <option value="bg-yellow-100">Yellow</option>
                        <option value="bg-red-100">Red</option>
                        <option value="bg-purple-100">Purple</option>
                      </select>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveColumn}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <SafeIcon icon={FiSave} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancelColumnEdit}
                          className="px-3 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                        >
                          <SafeIcon icon={FiX} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span className="font-medium">{column.title}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditColumn(column)}
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        >
                          <SafeIcon icon={FiEdit} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteColumn(column.id)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Kanban Board */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {columns.map((column) => {
            const projects = getProjectsByStatus(column.title);
            return (
              <div
                key={column.id}
                className="flex flex-col"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, column.title)}
              >
                {/* Column Header */}
                <div className={`${column.color} rounded-lg p-4 mb-4`}>
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">{column.title}</h4>
                    <span className="text-sm text-gray-600 bg-white px-2 py-1 rounded-full">
                      {projects.length}
                    </span>
                  </div>
                </div>

                {/* Projects */}
                <div className="space-y-3 flex-1 min-h-96">
                  <AnimatePresence>
                    {projects.map((project, index) => (
                      <motion.div
                        key={project.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ delay: index * 0.05 }}
                        draggable={editingProject !== project.id}
                        onDragStart={(e) => handleDragStart(e, project)}
                        className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-move"
                      >
                        {editingProject === project.id ? (
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={projectFormData.title}
                              onChange={(e) => setProjectFormData(prev => ({ ...prev, title: e.target.value }))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-medium"
                              placeholder="Project title"
                            />
                            <textarea
                              value={projectFormData.description}
                              onChange={(e) => setProjectFormData(prev => ({ ...prev, description: e.target.value }))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              rows={2}
                              placeholder="Description"
                            />
                            <textarea
                              value={projectFormData.notes}
                              onChange={(e) => setProjectFormData(prev => ({ ...prev, notes: e.target.value }))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              rows={2}
                              placeholder="Notes"
                            />
                            <select
                              value={projectFormData.owner}
                              onChange={(e) => setProjectFormData(prev => ({ ...prev, owner: e.target.value }))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Select Owner</option>
                              {allUsers.map(user => (
                                <option key={user.id} value={user.name}>{user.name}</option>
                              ))}
                            </select>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`ongoing-edit-${editingProject}`}
                                checked={projectFormData.ongoing}
                                onChange={(e) => setProjectFormData(prev => ({ ...prev, ongoing: e.target.checked, dueDate: e.target.checked ? '' : prev.dueDate }))}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <label htmlFor={`ongoing-edit-${editingProject}`} className="text-xs text-gray-700">Ongoing</label>
                            </div>
                            {!projectFormData.ongoing && (
                              <input
                                type="date"
                                value={projectFormData.dueDate}
                                onChange={(e) => setProjectFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                            )}
                            <select
                              value={projectFormData.priority}
                              onChange={(e) => setProjectFormData(prev => ({ ...prev, priority: e.target.value }))}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="low">Low Priority</option>
                              <option value="medium">Medium Priority</option>
                              <option value="high">High Priority</option>
                            </select>
                            <div className="flex gap-2">
                              <button
                                onClick={handleSaveProject}
                                className="flex items-center px-2 py-1 bg-green-600 text-white rounded text-xs"
                              >
                                <SafeIcon icon={FiSave} className="w-3 h-3 mr-1" />
                                Save
                              </button>
                              <button
                                onClick={handleCancelProjectForm}
                                className="flex items-center px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs"
                              >
                                <SafeIcon icon={FiX} className="w-3 h-3 mr-1" />
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start justify-between mb-3">
                              <h5 className="font-medium text-gray-900 text-sm line-clamp-2">
                                {project.title}
                              </h5>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setShowTaskModal(project.id)}
                                  className="p-1 text-green-600 hover:bg-green-100 rounded"
                                  title="Add Task"
                                >
                                  <SafeIcon icon={FiCheckSquare} className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleEditProject(project)}
                                  className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                >
                                  <SafeIcon icon={FiEdit} className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => deleteItem('projects', project.id)}
                                  className="p-1 text-red-600 hover:bg-red-100 rounded"
                                >
                                  <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                                </button>
                              </div>
                            </div>

                            <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                              {project.description}
                            </p>

                            <div className="flex gap-1 mb-3">
                              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(project.priority)}`}>
                                {project.priority}
                              </span>
                            </div>

                            <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                              <div className="flex items-center">
                                <SafeIcon icon={FiUser} className="w-3 h-3 mr-1" />
                                {project.owner}
                              </div>
                              <div className="flex items-center">
                                <SafeIcon icon={project.dueDate ? FiCalendar : FiInfinity} className="w-3 h-3 mr-1" />
                                {project.dueDate ? format(parseISO(project.dueDate), 'MMM dd') : 'Ongoing'}
                              </div>
                            </div>

                            {/* Tasks Preview */}
                            {project.tasks && Array.isArray(project.tasks) && project.tasks.length > 0 && (
                              <div className="mb-3">
                                <div className="text-xs text-gray-500 mb-1">
                                  Tasks ({project.tasks.filter(t => t.status === 'completed').length}/{project.tasks.length})
                                </div>
                                <div className="space-y-1 max-h-24 overflow-y-auto">
                                  {project.tasks.slice(0, 3).map(task => (
                                    <div key={task.id} className="flex items-center text-xs group">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleToggleTaskStatus(project, task); }}
                                        className={`mr-1 flex-shrink-0 ${task.status === 'completed' ? 'text-green-600' : 'text-gray-400'} hover:scale-110 transition-transform`}
                                        title={task.status === 'completed' ? 'Mark incomplete' : 'Mark complete'}
                                      >
                                        <SafeIcon
                                          icon={task.status === 'completed' ? FiCheckSquare : FiCheckSquare}
                                          className="w-3 h-3"
                                        />
                                      </button>
                                      <span className={`flex-1 truncate ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                        {task.title}
                                      </span>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleDeleteTask(project, task.id); }}
                                        className="p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                        title="Delete task"
                                      >
                                        <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                  {project.tasks.length > 3 && (
                                    <div className="text-xs text-gray-500">
                                      +{project.tasks.length - 3} more tasks
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Progress Bar */}
                            <div>
                              <div className="flex justify-between text-xs text-gray-600 mb-1">
                                <span>Progress</span>
                                <span>
                                  {project.tasks?.filter(t => t.status === 'completed').length || 0}/{project.tasks?.length || 0} tasks
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1">
                                <div
                                  className="bg-primary-500 h-1 rounded-full transition-all duration-300"
                                  style={{
                                    width: project.tasks?.length > 0 
                                      ? `${((project.tasks.filter(t => t.status === 'completed').length / project.tasks.length) * 100)}%`
                                      : '0%'
                                  }}
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {/* Add Project Button */}
                  {showProjectForm === column.id ? (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-4 space-y-3"
                    >
                      <input
                        type="text"
                        value={projectFormData.title}
                        onChange={(e) => setProjectFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="Project title *"
                      />
                      <textarea
                        value={projectFormData.description}
                        onChange={(e) => setProjectFormData(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        rows={2}
                        placeholder="Description *"
                      />
                      <textarea
                        value={projectFormData.notes}
                        onChange={(e) => setProjectFormData(prev => ({ ...prev, notes: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        rows={2}
                        placeholder="Notes"
                      />
                      <select
                        value={projectFormData.owner}
                        onChange={(e) => setProjectFormData(prev => ({ ...prev, owner: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="">Select Owner *</option>
                        {allUsers.map(user => (
                          <option key={user.id} value={user.name}>{user.name}</option>
                        ))}
                      </select>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="ongoing-new"
                          checked={projectFormData.ongoing}
                          onChange={(e) => setProjectFormData(prev => ({ ...prev, ongoing: e.target.checked, dueDate: e.target.checked ? '' : prev.dueDate }))}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <label htmlFor="ongoing-new" className="text-sm text-gray-700">Ongoing (no due date)</label>
                      </div>
                      {!projectFormData.ongoing && (
                        <input
                          type="date"
                          value={projectFormData.dueDate}
                          onChange={(e) => setProjectFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      )}
                      <select
                        value={projectFormData.priority}
                        onChange={(e) => setProjectFormData(prev => ({ ...prev, priority: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value="low">Low Priority</option>
                        <option value="medium">Medium Priority</option>
                        <option value="high">High Priority</option>
                      </select>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAddProject(column.title)}
                          className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors"
                        >
                          <SafeIcon icon={FiSave} className="w-4 h-4 mr-1" />
                          Add Project
                        </button>
                        <button
                          onClick={handleCancelProjectForm}
                          className="flex items-center px-3 py-2 bg-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-400 transition-colors"
                        >
                          <SafeIcon icon={FiX} className="w-4 h-4 mr-1" />
                          Cancel
                        </button>
                      </div>
                    </motion.div>
                  ) : (
                    <button
                      onClick={() => setShowProjectForm(column.id)}
                      className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <SafeIcon icon={FiPlus} className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-sm">Add Project</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Ongoing Projects Section */}
        {ongoingProjects.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="bg-cyan-100 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <SafeIcon icon={FiInfinity} className="w-5 h-5 text-cyan-700 mr-2" />
                  <h4 className="font-semibold text-gray-900">Ongoing Projects</h4>
                </div>
                <span className="text-sm text-gray-600 bg-white px-2 py-1 rounded-full">
                  {ongoingProjects.length}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <AnimatePresence>
                {ongoingProjects.map((project, index) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h5 className="font-medium text-gray-900 text-sm line-clamp-2">
                        {project.title}
                      </h5>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setShowTaskModal(project.id)}
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          title="Add Task"
                        >
                          <SafeIcon icon={FiCheckSquare} className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => handleEditProject(project)}
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        >
                          <SafeIcon icon={FiEdit} className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => deleteItem('projects', project.id)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                        </button>
                      </div>
                    </div>

                    <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                      {project.description}
                    </p>

                    <div className="flex gap-1 mb-3">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(project.priority)}`}>
                        {project.priority}
                      </span>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                      <div className="flex items-center">
                        <SafeIcon icon={FiUser} className="w-3 h-3 mr-1" />
                        {project.owner}
                      </div>
                      <div className="flex items-center text-cyan-600">
                        <SafeIcon icon={FiInfinity} className="w-3 h-3 mr-1" />
                        Ongoing
                      </div>
                    </div>

                    {/* Tasks Preview */}
                    {project.tasks && Array.isArray(project.tasks) && project.tasks.length > 0 && (
                      <div className="mb-3">
                        <div className="text-xs text-gray-500 mb-1">
                          Tasks ({project.tasks.filter(t => t.status === 'completed').length}/{project.tasks.length})
                        </div>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {project.tasks.slice(0, 3).map(task => (
                            <div key={task.id} className="flex items-center text-xs group">
                              <button
                                onClick={() => handleToggleTaskStatus(project, task)}
                                className={`w-3 h-3 mr-1 flex-shrink-0 ${task.status === 'completed' ? 'text-green-600' : 'text-gray-400'} hover:scale-110 transition-transform`}
                              >
                                <SafeIcon
                                  icon={FiCheckSquare}
                                  className="w-3 h-3"
                                />
                              </button>
                              <span className={`flex-1 truncate ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                {task.title}
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteTask(project, task.id); }}
                                className="p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                title="Delete task"
                              >
                                <SafeIcon icon={FiTrash2} className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                          {project.tasks.length > 3 && (
                            <div className="text-xs text-gray-500">
                              +{project.tasks.length - 3} more tasks
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Progress Bar */}
                    <div>
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>
                          {project.tasks?.filter(t => t.status === 'completed').length || 0}/{project.tasks?.length || 0} tasks
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1">
                        <div
                          className="bg-cyan-500 h-1 rounded-full transition-all duration-300"
                          style={{
                            width: project.tasks?.length > 0
                              ? `${((project.tasks.filter(t => t.status === 'completed').length / project.tasks.length) * 100)}%`
                              : '0%'
                          }}
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Summary Stats */}
        {filteredProjects.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-4">
              Project Summary {selectedAssignee !== 'all' && `(${selectedAssignee}'s tasks)`}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <SafeIcon icon={FiTarget} className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-xl font-bold text-blue-600">
                  {filteredProjects.filter(p => p.status === 'planning' && p.dueDate).length}
                </p>
                <p className="text-sm text-gray-600">Planning</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <SafeIcon icon={FiTarget} className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                <p className="text-xl font-bold text-yellow-600">
                  {filteredProjects.filter(p => p.status === 'in-progress' && p.dueDate).length}
                </p>
                <p className="text-sm text-gray-600">In Progress</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <SafeIcon icon={FiTarget} className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="text-xl font-bold text-purple-600">
                  {filteredProjects.filter(p => p.status === 'review' && p.dueDate).length}
                </p>
                <p className="text-sm text-gray-600">Review</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <SafeIcon icon={FiTarget} className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-xl font-bold text-green-600">
                  {filteredProjects.filter(p => p.status === 'completed' && p.dueDate).length}
                </p>
                <p className="text-sm text-gray-600">Completed</p>
              </div>
              <div className="text-center p-4 bg-cyan-50 rounded-lg">
                <SafeIcon icon={FiInfinity} className="w-8 h-8 text-cyan-600 mx-auto mb-2" />
                <p className="text-xl font-bold text-cyan-600">
                  {ongoingProjects.length}
                </p>
                <p className="text-sm text-gray-600">Ongoing</p>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Create Task Modal */}
      <CreateTaskModal
        isOpen={showTaskModal !== null}
        onClose={() => setShowTaskModal(null)}
        projectId={showTaskModal}
        onSuccess={() => {
          console.log('Task created successfully!');
        }}
      />
    </>
  );
};

export default ProjectBoard;